import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CUSTOM_MIME_TYPES } from '@/helpers/enums';
import docsMutation from '@/api/mutitations/docs';
import {
  Text,
  Flex,
  Stack,
  ThemeIcon,
  Badge,
  Loader,
  ScrollArea,
  Button,
  Box,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCircleXFilled, IconCircleCheckFilled, IconClock, IconEye, IconPencil,
  IconChartPie, IconReceipt,
} from '@tabler/icons-react';
import PropTypes from 'prop-types';
import prepareTranslate from '@/helpers/dictionary';
import CustomDropZone from '@/components/CustomDropZone';
import PageTemplate from '@/components/PageTemplate';
import Section from '@/components/Section';
import routes from '@/routes';
import clsx from 'clsx';
import styles from './style.module.css';

const ACCEPTED_TYPES = [CUSTOM_MIME_TYPES.xlsx, CUSTOM_MIME_TYPES.xls, CUSTOM_MIME_TYPES.xlsb];

const FILE_TYPE_OPTIONS = [
  {
    value: 'annualAccumulation',
    label: 'היקף',
    description: 'קבצי היקף מחברות הביטוח',
    uploadTitle: 'גרור לכאן קבצי היקף, או לחץ לבחירה',
    Icon: IconChartPie,
  },
  {
    value: 'monthly',
    label: 'נפרעים',
    description: 'קבצי נפרעים ועמלות שוטפות מהחברות',
    uploadTitle: 'גרור לכאן קבצי נפרעים, או לחץ לבחירה',
    Icon: IconReceipt,
  },
];

// ─── Error row ─────────────────────────────────────────────────────────────────

// Filename problems are saved as events, where the user completes the missing details
const FILENAME_ERROR_CODES = [
  'NO_COMPANY', 'NO_YEAR', 'NO_RULE', 'AMBIGUOUS', 'NO_MONTH', 'NO_REFERENCE_YEAR',
];

const FileErrorRow = ({ result }) => {
  const { name, error, code } = result;
  const navigate = useNavigate();
  return (
    <Flex align="flex-start" gap="sm" className={styles.result_row}>
      <ThemeIcon variant="white"><IconCircleXFilled color="red" size={20} /></ThemeIcon>
      <Stack gap={2}>
        <Text size="sm" c="red">{name}</Text>
        {error && <Text size="xs" c="red">{error}</Text>}
      </Stack>
      {FILENAME_ERROR_CODES.includes(code) && (
        <Button
          size="xs"
          variant="outline"
          color="red"
          mr="auto"
          leftSection={<IconPencil size={14} />}
          onClick={() => navigate(routes.events.path)}
        >
          מעבר לתיקון
        </Button>
      )}
    </Flex>
  );
};

FileErrorRow.propTypes = {
  result: PropTypes.shape({
    name: PropTypes.string.isRequired,
    error: PropTypes.string,
    code: PropTypes.string,
  }).isRequired,
};

// ─── Result row (uploading / accepted / waiting) ────────────────────────────────

const FileResultRow = ({ result }) => {
  const {
    name, status, companyName, ruleName, year, month,
    receivedCount, expectedCount, receivedFiles, processedFiles,
  } = result;

  if (status === 'uploading') {
    return (
      <Flex align="center" gap="sm" className={styles.result_row}>
        <Loader size={20} />
        <Text size="sm" c="dimmed">{name}</Text>
      </Flex>
    );
  }

  if (status === 'waiting') {
    return (
      <Flex align="flex-start" gap="sm" className={styles.result_row}>
        <ThemeIcon variant="white"><IconClock color="orange" size={20} /></ThemeIcon>
        <Stack gap={2}>
          <Text size="sm" fw={500}>{name}</Text>
          <Flex gap="xs" wrap="wrap">
            {companyName && <Badge size="xs" variant="light" color="blue">{companyName}</Badge>}
            {ruleName && <Badge size="xs" variant="light" color="gray">{ruleName}</Badge>}
            <Badge size="xs" variant="light" color="orange">
              {`${receivedCount}/${expectedCount} קבצים התקבלו`}
            </Badge>
          </Flex>
          {receivedFiles?.length > 0 && (
            <Text size="xs" c="dimmed">
              {`התקבל: ${receivedFiles.join(', ')}`}
            </Text>
          )}
          <Text size="xs" c="dimmed">ממתין לקבצים הנוספים</Text>
        </Stack>
      </Flex>
    );
  }

  // accepted
  return (
    <Flex align="flex-start" gap="sm" className={styles.result_row}>
      <ThemeIcon variant="white"><IconCircleCheckFilled color="green" size={20} /></ThemeIcon>
      <Stack gap={2} className={styles.result_content}>
        <Text size="sm" fw={500}>{name}</Text>
        <Flex gap="xs" wrap="wrap">
          {companyName && <Badge size="xs" variant="light" color="blue">{companyName}</Badge>}
          {ruleName && <Badge size="xs" variant="light" color="gray">{ruleName}</Badge>}
          {year && (
            <Badge size="xs" variant="light" color="gray">
              {month ? `${month}/${year}` : year}
            </Badge>
          )}
        </Flex>
        {(() => {
          if (!(processedFiles?.length > 1)) return null;
          return (
            <Stack gap={2}>
              <Text size="xs" c="orange.7" fw={500}>מעבד ביחד:</Text>
              {processedFiles.map((f, i) => (
                <Tooltip key={i} label={f} withArrow position="top-start" multiline maw={360}>
                  <Text
                    size="xs"
                    c="orange.6"
                    className={styles.processed_file}
                  >
                    {f}
                  </Text>
                </Tooltip>
              ))}
            </Stack>
          );
        })()}
        <Text size="xs" c="dimmed">הקובץ מעובד ברקע, ותוצג התראה כשהעיבוד יסתיים</Text>
      </Stack>
    </Flex>
  );
};

FileResultRow.propTypes = {
  result: PropTypes.shape({
    name: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['uploading', 'accepted', 'waiting']).isRequired,
    companyName: PropTypes.string,
    ruleName: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    receivedCount: PropTypes.number,
    expectedCount: PropTypes.number,
    receivedFiles: PropTypes.arrayOf(PropTypes.string),
    processedFiles: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

// ─── Main page ─────────────────────────────────────────────────────────────────

const Import = () => {
  const t = prepareTranslate();
  const navigate = useNavigate();
  const [fileResults, setFileResults] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [commissionFileType, setCommissionFileType] = useState(null);
  const { mutateAsync: importAuto } = docsMutation.useImportAuto();

  const enqueueFiles = (droppedFiles) => {
    droppedFiles.forEach((file) => {
      const id = `${file.name}_${Date.now()}_${Math.random()}`;

      // Show loader immediately while uploading
      setFileResults((prev) => [{ id, name: file.name, status: 'uploading' }, ...prev]);

      const formData = new FormData();
      formData.append('files', file);
      formData.append('requestData', JSON.stringify({ commissionFileType }));

      importAuto(formData)
        .then((data) => {
          setFileResults((prev) => {
            const withCurrent = prev.map((entry) => (entry.id !== id ? entry : {
              ...entry,
              status: data?.waiting ? 'waiting' : 'accepted',
              companyName: data?.companyName || '',
              ruleName: data?.ruleName || '',
              year: data?.year,
              month: data?.month,
              receivedCount: data?.receivedCount,
              expectedCount: data?.expectedCount,
              receivedFiles: data?.receivedFiles,
              processedFiles: data?.processedFiles || [],
            }));

            // The last file of a rule with several files completes the earlier, waiting ones
            if (!(data?.processedFiles?.length > 1)) return withCurrent;

            return withCurrent.map((entry) => (
              entry.status === 'waiting' && entry.ruleName === data.ruleName
                ? { ...entry, status: 'accepted', processedFiles: data.processedFiles }
                : entry
            ));
          });
        })
        .catch((err) => {
          const msg = err?.error?.message || err?.data?.message || err?.message || 'שגיאה בעיבוד הקובץ';
          setFileResults((prev) => prev.filter((entry) => entry.id !== id));
          setFileErrors((prev) => [{
            id, name: file.name, error: msg, code: err?.error?.code,
          }, ...prev]);
        });
    });
  };

  const actions = [
    <Button
      key={'manual'}
      variant="outline"
      leftSection={<IconPencil />}
      onClick={() => navigate(routes.manualImports.path)}
    >
      {routes.manualImports.name}
    </Button>,
    <Button
      key={'files'}
      leftSection={<IconEye />}
      onClick={() => navigate(routes.files.path)}
    >
      {t('pages.import.action')}
    </Button>,
  ];

  const selectedType = FILE_TYPE_OPTIONS.find((o) => o.value === commissionFileType);
  const hasResults = fileResults.length > 0;
  const hasErrors = fileErrors.length > 0;

  return (
    <PageTemplate title="טעינת נתונים" actions={actions}>
      <Stack gap="md" mt={16}>
        <Section>
          <Text size="sm" c="dimmed" mt="md">שלב 1 מתוך 2: בחירת סוג קבצים</Text>
          <Flex gap="md" py="md" wrap="wrap">
            {FILE_TYPE_OPTIONS.map(({
              value, label, description, Icon,
            }) => (
              <UnstyledButton
                key={value}
                className={clsx(
                  styles.type_card,
                  commissionFileType === value && styles.type_card_selected,
                )}
                onClick={() => setCommissionFileType(value)}
                aria-pressed={commissionFileType === value}
              >
                <Icon size={28} stroke={1.5} />
                <Stack gap={2}>
                  <Text fw={600}>{label}</Text>
                  <Text size="sm" c="dimmed">{description}</Text>
                </Stack>
              </UnstyledButton>
            ))}
          </Flex>
        </Section>

        {selectedType && (
          <Section>
            <Text size="sm" c="dimmed" mt="md">שלב 2 מתוך 2: העלאת הקבצים</Text>
            <Box py="lg">
              <CustomDropZone
                handleAccept={enqueueFiles}
                outsideAccept={ACCEPTED_TYPES}
                title={selectedType.uploadTitle}
                showFormats
              />
            </Box>
          </Section>
        )}

        {(hasResults || hasErrors) && (
          <Section title="העלאות אחרונות">
            <ScrollArea>
              <Stack gap={0}>
                {fileResults.map((result) => (
                  <FileResultRow key={result.id} result={result} />
                ))}
                {fileErrors.map((result) => (
                  <FileErrorRow key={result.id} result={result} />
                ))}
              </Stack>
            </ScrollArea>
          </Section>
        )}
      </Stack>
    </PageTemplate>
  );
};

export default Import;
